/**
 * Administração → WhatsBI → **Conversas** (`W-15`, ADR-050): quem perguntou o quê.
 *
 * O administrador vê as conversas da organização com o agente — na tela e no WhatsApp —, por
 * pessoa, canal, período e texto, e abre qualquer uma para ler como foi: as perguntas e as
 * respostas, com os gráficos e as tabelas que a pessoa viu, e os tokens que a conversa gastou.
 *
 * **Ler a conversa de outra pessoa é acesso a dado dela**: a lista mostra só a primeira
 * pergunta; abrir a conversa inteira fica na trilha administrativa (`whatsbi.conversa_lida`),
 * e a tela diz isso antes do clique. Quem pergunta também sabe: o painel do agente avisa que
 * o administrador pode ler.
 *
 * Embaixo, o prazo de guarda (`W-09`): é dele que depende até quando esta lista volta.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import type { FormEvent } from "react";

import { ErroDaApi, api } from "../api/cliente";
import type {
  ConversaDaOrganizacao,
  ConversaDaOrganizacaoInteira,
  ConversasDaOrganizacao,
  PessoaDaConversa,
} from "../api/tipos";
import { Badge, Botao, Estado, Painel } from "../componentes/base";
import { RespostaNaTela } from "../componentes/CelulaDoAgente";
import { turnosDaConversa } from "../componentes/conversa";
import { Icone } from "../componentes/icones";
import { dataCurta, dataEHora, numeroLongo } from "../formato";
import { Guarda } from "./WhatsBiCockpit";

const ENTRADA =
  "min-h-8 w-full rounded-botao border border-linha bg-painel px-2 text-[11.5px] text-tinta focus:border-azul";

interface Filtros {
  pessoa: string;
  canal: "" | "cockpit" | "whatsapp";
  desde: string;
  ate: string;
  busca: string;
}

const SEM_FILTRO: Filtros = {
  pessoa: "",
  canal: "",
  desde: "",
  ate: "",
  busca: "",
};

function consulta(filtros: Filtros, pagina: number): string {
  const parametros = new URLSearchParams();
  for (const [chave, valor] of Object.entries(filtros)) {
    if (valor.trim()) parametros.set(chave, valor.trim());
  }
  if (pagina > 0) parametros.set("pagina", String(pagina));
  const texto = parametros.toString();
  return `/whatsbi/organizacao/conversas${texto ? `?${texto}` : ""}`;
}

function CanalDaConversa({ canal }: { canal: string }) {
  return canal === "whatsapp" ? (
    <Badge cor="verde">WhatsApp</Badge>
  ) : (
    <Badge cor="azul">Tela</Badge>
  );
}

export function ConversasDoWhatsBi() {
  const [filtros, definirFiltros] = useState<Filtros>(SEM_FILTRO);
  // O que está valendo na lista: a busca só vale quando a pessoa manda.
  const [aplicados, definirAplicados] = useState<Filtros>(SEM_FILTRO);
  const [conversas, definirConversas] = useState<
    ConversaDaOrganizacao[] | null
  >(null);
  const [pessoas, definirPessoas] = useState<PessoaDaConversa[]>([]);
  const [mais, definirMais] = useState(false);
  const [pagina, definirPagina] = useState(0);
  const [erro, definirErro] = useState<ErroDaApi | null>(null);
  const [aberta, definirAberta] = useState<ConversaDaOrganizacaoInteira | null>(
    null,
  );
  const [abrindo, definirAbrindo] = useState<string | null>(null);

  const carregar = useCallback(async (quais: Filtros, qual: number) => {
    try {
      const lidas = await api<ConversasDaOrganizacao>(consulta(quais, qual));
      definirConversas((antes) =>
        qual === 0 ? lidas.conversas : [...(antes ?? []), ...lidas.conversas],
      );
      definirPessoas(lidas.pessoas);
      definirMais(lidas.mais);
      definirPagina(qual);
      definirErro(null);
    } catch (falha) {
      definirErro(
        falha instanceof ErroDaApi
          ? falha
          : new ErroDaApi("Falha inesperada.", 0),
      );
    }
  }, []);

  useEffect(() => {
    void carregar(aplicados, 0);
  }, [aplicados, carregar]);

  function aplicar(e?: FormEvent) {
    e?.preventDefault();
    definirConversas(null);
    definirAplicados(filtros);
  }

  function mudar<K extends keyof Filtros>(chave: K, valor: Filtros[K]) {
    const novos = { ...filtros, [chave]: valor };
    definirFiltros(novos);
    // Escolher pessoa, canal ou dia já filtra; o texto da busca espera o Enter.
    if (chave !== "busca") {
      definirConversas(null);
      definirAplicados(novos);
    }
  }

  async function abrir(id: string) {
    definirAbrindo(id);
    try {
      definirAberta(
        await api<ConversaDaOrganizacaoInteira>(
          `/whatsbi/organizacao/conversas/${id}`,
        ),
      );
    } catch (falha) {
      definirErro(
        falha instanceof ErroDaApi
          ? falha
          : new ErroDaApi("Falha inesperada.", 0),
      );
    } finally {
      definirAbrindo(null);
    }
  }

  if (erro && conversas === null) {
    return (
      <Painel titulo="Conversas com o agente">
        <Estado
          tipo={
            erro.semLicenca
              ? "sem-licenca"
              : erro.status === 403
                ? "bloqueado"
                : "erro"
          }
          titulo={
            erro.semLicenca
              ? "Esta organização não licenciou o WhatsBI Cockpit."
              : erro.status === 403
                ? "Esta área é do administrador da organização."
                : "Não foi possível carregar as conversas."
          }
          detalhe={erro.message}
        />
      </Painel>
    );
  }

  const filtrando = Object.values(aplicados).some((v) => v.trim());

  return (
    <div className="grid gap-3">
      <Painel
        titulo="Conversas com o agente"
        acoes={
          <span className="inline-flex items-center gap-1.5 text-[10.5px] text-tinta-3">
            <Icone.Escudo />
            Abrir uma conversa fica na trilha
          </span>
        }
      >
        <form
          onSubmit={aplicar}
          aria-label="Filtrar as conversas"
          className="grid gap-2 [grid-template-columns:repeat(auto-fit,minmax(150px,1fr))]"
        >
          <label className="grid gap-1 text-[11px] text-tinta-3">
            Pessoa
            <select
              value={filtros.pessoa}
              onChange={(e) => mudar("pessoa", e.target.value)}
              className={ENTRADA}
            >
              <option value="">Todas</option>
              {pessoas.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nome}
                </option>
              ))}
            </select>
          </label>
          <label className="grid gap-1 text-[11px] text-tinta-3">
            Canal
            <select
              value={filtros.canal}
              onChange={(e) =>
                mudar("canal", e.target.value as Filtros["canal"])
              }
              className={ENTRADA}
            >
              <option value="">Tela e WhatsApp</option>
              <option value="cockpit">Tela do cockpit</option>
              <option value="whatsapp">WhatsApp</option>
            </select>
          </label>
          <label className="grid gap-1 text-[11px] text-tinta-3">
            De
            <input
              type="date"
              value={filtros.desde}
              onChange={(e) => mudar("desde", e.target.value)}
              className={ENTRADA}
            />
          </label>
          <label className="grid gap-1 text-[11px] text-tinta-3">
            Até
            <input
              type="date"
              value={filtros.ate}
              onChange={(e) => mudar("ate", e.target.value)}
              className={ENTRADA}
            />
          </label>
          <label className="grid gap-1 text-[11px] text-tinta-3">
            Texto da conversa
            <span className="flex gap-1">
              <input
                type="search"
                maxLength={100}
                value={filtros.busca}
                placeholder="Ex.: arrecadação"
                onChange={(e) => mudar("busca", e.target.value)}
                className={ENTRADA}
              />
              <Botao type="submit" aria-label="Buscar">
                <Icone.Lupa />
              </Botao>
            </span>
          </label>
        </form>
        {filtrando && (
          <Botao
            onClick={() => {
              definirFiltros(SEM_FILTRO);
              definirConversas(null);
              definirAplicados(SEM_FILTRO);
            }}
          >
            Limpar os filtros
          </Botao>
        )}

        <div className="grid grid-cols-1 gap-3 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] lg:items-start">
          {/* No celular, a conversa aberta toma o lugar da lista. */}
          <div className={aberta ? "hidden lg:grid" : "grid"}>
            {conversas === null ? (
              <Estado tipo="carregando" titulo="Carregando…" />
            ) : conversas.length === 0 ? (
              <p className="rounded-cartao border border-linha bg-painel-2 px-2.5 py-3 text-[11.5px] text-tinta-3">
                {filtrando
                  ? "Nenhuma conversa com esses filtros."
                  : "Ninguém conversou com o agente ainda — ou as conversas já passaram do prazo de guarda."}
              </p>
            ) : (
              <ul
                aria-label="Conversas da organização"
                className="grid gap-1.5"
              >
                {conversas.map((c) => (
                  <li key={c.id}>
                    <button
                      type="button"
                      onClick={() => void abrir(c.id)}
                      aria-current={aberta?.id === c.id ? "true" : undefined}
                      disabled={abrindo !== null}
                      className={`grid w-full gap-0.5 rounded-cartao border px-2.5 py-2 text-left hover:bg-realce ${
                        aberta?.id === c.id
                          ? "border-azul-borda bg-azul-fundo"
                          : "border-linha bg-painel-2"
                      }`}
                    >
                      <span className="flex items-center gap-1.5">
                        <b className="min-w-0 flex-1 truncate text-[12px] text-tinta">
                          {c.pessoa.nome}
                        </b>
                        <CanalDaConversa canal={c.canal} />
                      </span>
                      <span className="line-clamp-2 text-[11.5px] text-tinta-2">
                        {c.primeira_pergunta}
                      </span>
                      <span className="text-[10.5px] text-tinta-3">
                        {dataCurta(c.ultima_em)} ·{" "}
                        {c.perguntas === 1
                          ? "1 pergunta"
                          : `${c.perguntas} perguntas`}
                        {c.tokens > 0 && ` · ${numeroLongo(c.tokens)} tokens`}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {mais && conversas && (
              <Botao onClick={() => void carregar(aplicados, pagina + 1)}>
                Ver mais antigas
              </Botao>
            )}
          </div>

          {aberta ? (
            <ConversaAberta
              conversa={aberta}
              aoFechar={() => definirAberta(null)}
            />
          ) : (
            <p className="hidden rounded-cartao border border-dashed border-linha px-3 py-6 text-center text-[11.5px] text-tinta-3 lg:block">
              Escolha uma conversa para ler. A leitura fica registrada na trilha
              administrativa, com quem leu e quando.
            </p>
          )}
        </div>
      </Painel>
      <Guarda />
    </div>
  );
}

function ConversaAberta({
  conversa,
  aoFechar,
}: {
  conversa: ConversaDaOrganizacaoInteira;
  aoFechar: () => void;
}) {
  const turnos = turnosDaConversa(conversa);
  const topo = useRef<HTMLElement>(null);
  // No celular, a conversa abre embaixo dos filtros: a tela vai até ela.
  useEffect(() => {
    topo.current?.scrollIntoView?.({ block: "start", behavior: "smooth" });
  }, [conversa.id]);
  return (
    <section
      ref={topo}
      aria-label={`Conversa de ${conversa.pessoa.nome}`}
      className="grid min-w-0 grid-cols-1 content-start gap-2 rounded-cartao border border-linha bg-painel p-2.5"
    >
      <header className="flex flex-wrap items-start gap-2">
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-[12.5px] font-bold text-tinta">
            {conversa.pessoa.nome}
          </h3>
          <p className="truncate text-[11px] text-tinta-3">
            {conversa.pessoa.email} · iniciada em{" "}
            {dataEHora(conversa.iniciada_em)} · {numeroLongo(conversa.tokens)}{" "}
            tokens
          </p>
        </div>
        <CanalDaConversa canal={conversa.canal} />
        <Botao onClick={aoFechar}>
          <span className="lg:hidden">← Voltar à lista</span>
          <span className="hidden lg:inline">Fechar</span>
        </Botao>
      </header>
      <p className="text-[10.5px] text-tinta-3">
        Esta leitura ficou registrada na trilha administrativa.
      </p>
      <div className="grid min-w-0 grid-cols-1 gap-3">
        {turnos.map((t, i) => (
          <div key={i} className="grid min-w-0 grid-cols-1 gap-1.5">
            <p className="max-w-[85%] justify-self-end rounded-cartao border border-azul-borda bg-azul-fundo px-2.5 py-1.5 text-[12px] text-tinta">
              {t.pergunta}
            </p>
            {t.resposta ? (
              <RespostaNaTela resposta={t.resposta} />
            ) : (
              <p className="text-[11.5px] text-tinta-3">{t.erro}</p>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
